// =============================================================
// MapView.jsx — Global interactive map
// Layers: Real Estate parcels (polygons + pins) | Loan collateral parcels
// Filters: individual entity toggle, property type filter
// =============================================================
import { useState, useEffect, useRef, useCallback } from 'react';
import { B } from '../constants/brand';
import { fmt } from '../utils/format';
import { api } from '../api/client';
import { Spinner, Icon } from '../components/ui';
import { MAPBOX_TOKEN } from '../components/shared/ParcelsMapTab';
import { MapLayerCatalog } from '../components/shared/MapLayerCatalog';
import { BASEMAP_BY_ID, DEFAULT_BASEMAP_ID } from '../constants/mapLayers';
import {
  applyBasemap, addOverlay, removeOverlay, setOverlayOpacity, reapplyOverlays,
  enableTerrain, disableTerrain, setTerrainExaggeration,
} from '../utils/mapLayerEngine';
import { setupAnnotationLayers, removeAnnotationLayers } from '../components/shared/MapAnnotations';

// ── CDN loaders (shared pattern from ParcelsMapTab) ───────────
function loadScript(src, onload, onerror) {
  if (document.querySelector(`script[src="${src}"]`)) { onload(); return; }
  const s = document.createElement('script');
  s.src = src; s.onload = onload;
  if (onerror) s.onerror = onerror;
  document.head.appendChild(s);
}
function loadLink(href) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const l = document.createElement('link');
  l.rel = 'stylesheet'; l.href = href;
  document.head.appendChild(l);
}

const MB_VERSION = '3.3.0';
const MB_JS  = `https://cdn.jsdelivr.net/npm/mapbox-gl@${MB_VERSION}/dist/mapbox-gl.js`;
const MB_CSS = `https://cdn.jsdelivr.net/npm/mapbox-gl@${MB_VERSION}/dist/mapbox-gl.css`;

// ── Layer config — keyed by DetailType ───────────────────────
const LAYERS = {
  'R/E':     { label: 'Real Estate',       color: B.navy,  colorHex: '#234c5a' },
  'N/R':     { label: 'Note / Loan',       color: B.amber, colorHex: '#c47d1a' },
  'Active':  { label: 'Active Investment', color: B.green, colorHex: '#2d7a4f' },
  'Passive': { label: 'Passive Inv.',      color: B.textMd, colorHex: '#6b7280' },
};

// ── Popup HTML ────────────────────────────────────────────────
function markerPopupHTML(m) {
  const layerCfg = LAYERS[m.detailType] || {};
  const value    = m.entityValue != null ? fmt.currency(m.entityValue) : '—';
  const acres    = m.regridAcres;
  return `
    <div style="font-family:'Inter',system-ui,-apple-system,'Segoe UI',sans-serif;min-width:180px;">
      <div style="font-size:11px;font-weight:700;letter-spacing:1px;text-transform:uppercase;color:${layerCfg.colorHex || '#234c5a'};margin-bottom:4px;">
        ${layerCfg.label || m.detailType || '—'}
      </div>
      <div style="font-size:14px;font-weight:700;color:#1a2e35;margin-bottom:6px;line-height:1.3;">
        ${m.dealName || '—'}
      </div>
      <div style="display:grid;grid-template-columns:auto 1fr;gap:2px 10px;font-size:12px;color:#4a6572;">
        <span>Status</span><span style="color:#1a2e35;font-weight:600;">${m.dealStatus || m.detailStatus || '—'}</span>
        <span>Value</span><span style="color:#1a2e35;font-weight:600;">${value}</span>
        ${acres ? `<span>Acres</span><span style="color:#1a2e35;font-weight:600;">${parseFloat(acres).toFixed(2)}</span>` : ''}
      </div>
    </div>
  `;
}

// ── Main Component ────────────────────────────────────────────
export const MapView = () => {
  const mapContainer = useRef(null);
  const mapRef       = useRef(null);
  const popupRef     = useRef(null);
  const markersRef   = useRef([]);

  const [mapReady,      setMapReady]      = useState(false);
  const [markers,       setMarkers]       = useState(null);
  const [geometries,    setGeometries]    = useState(null);
  const [annotations,   setAnnotations]   = useState(null);
  const [showAnnotations, setShowAnnotations] = useState(true);
  const [loading,       setLoading]       = useState(true);
  const [error,         setError]         = useState(null);
  const [layers,        setLayers]        = useState({ 'R/E': true, 'N/R': true, 'Active': true, 'Passive': true });
  const [showFilter,    setShowFilter]    = useState(false);
  // dealFilter: Set of dealIds to show (null = show all)
  const [dealFilter,    setDealFilter]    = useState(null);

  // ── Layer catalog state ───────────────────────────────────
  const [showLayers,    setShowLayers]    = useState(false);
  const [basemapId,     setBasemapId]     = useState(DEFAULT_BASEMAP_ID);
  // overlays: { [overlayId]: { overlay, on, opacity } }
  const [overlays,      setOverlays]      = useState({});
  const [terrain,       setTerrain]       = useState({ on: false, exaggeration: 1.4 });
  // Refs mirror state so the post-basemap-swap rebuild reads fresh values.
  const overlaysRef = useRef(overlays);
  const terrainRef  = useRef(terrain);
  overlaysRef.current = overlays;
  terrainRef.current  = terrain;

  // ── Fetch markers + geometries ────────────────────────────
  useEffect(() => {
    Promise.all([
      api.get('map/markers'),
      api.get('map/geometries'),
      api.get('map/annotations').catch(() => ({ type: 'FeatureCollection', features: [] })),
    ])
      .then(([m, g, a]) => { setMarkers(m); setGeometries(g); setAnnotations(a); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // ── Derived: unique deals for filter panel ────────────────
  const dealList = markers ? Object.values(
    markers.reduce((acc, m) => {
      if (!acc[m.dealId]) acc[m.dealId] = { dealId: m.dealId, dealName: m.dealName, detailType: m.detailType };
      return acc;
    }, {})
  ) : [];

  // ── isVisible helper ──────────────────────────────────────
  const isVisible = useCallback((m) => {
    if (!layers[m.detailType]) return false;
    if (dealFilter && !dealFilter.has(m.dealId)) return false;
    return true;
  }, [layers, dealFilter]);

  // ── Init map ──────────────────────────────────────────────
  useEffect(() => {
    if (mapRef.current || !mapContainer.current || !MAPBOX_TOKEN) {
      if (!MAPBOX_TOKEN) setMapReady('no-token');
      return;
    }
    loadLink(MB_CSS);
    loadScript(MB_JS, () => {
      if (!mapContainer.current) return;
      try {
        window.mapboxgl.accessToken = MAPBOX_TOKEN;
        const map = new window.mapboxgl.Map({
          container: mapContainer.current,
          style:     BASEMAP_BY_ID[DEFAULT_BASEMAP_ID].style,
          center:    [-98.5, 39.5],
          zoom:      5,
        });
        map.addControl(new window.mapboxgl.NavigationControl(), 'top-right');
        map.addControl(new window.mapboxgl.FullscreenControl(),  'top-right');
        map.addControl(new window.mapboxgl.ScaleControl(),       'bottom-left');
        mapRef.current = map;
        map.on('load', () => setMapReady(true));
      } catch (err) {
        // new mapboxgl.Map() throws synchronously if the render worker can't be
        // created (e.g. a CSP that blocks its blob: worker).
        console.error('Mapbox failed to initialize:', err);
        setError('The map failed to load. A browser extension or content-security policy may be blocking Mapbox.');
      }
    }, () => {
      console.error('Failed to load Mapbox GL JS from the CDN.');
      setError('The map failed to load. Check your network connection and reload.');
    });
    return () => {
      if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    };
  }, []);

  // ── Setup/update polygon layers ───────────────────────────
  const setupPolygons = useCallback(() => {
    const map = mapRef.current;
    if (!map || !geometries) return;

    const existing = map.getSource('parcel-polygons');
    if (existing) {
      existing.setData(geometries);
    } else {
      map.addSource('parcel-polygons', { type: 'geojson', data: geometries });
      map.addLayer({
        id: 'parcel-polygons-fill',
        type: 'fill',
        source: 'parcel-polygons',
        paint: {
          'fill-color': [
            'match', ['get', 'detailType'],
            'R/E',    '#234c5a',
            'N/R',    '#c47d1a',
            'Active', '#2d7a4f',
            '#6b7280',
          ],
          'fill-opacity': 0.3,
        },
      });
      map.addLayer({
        id: 'parcel-polygons-outline',
        type: 'line',
        source: 'parcel-polygons',
        paint: {
          'line-color': [
            'match', ['get', 'detailType'],
            'R/E',    '#234c5a',
            'N/R',    '#c47d1a',
            'Active', '#2d7a4f',
            '#6b7280',
          ],
          'line-width': 2,
        },
      });

      // Polygon click → popup
      map.on('click', 'parcel-polygons-fill', (e) => {
        const props = e.features[0]?.properties;
        if (!props) return;
        if (popupRef.current) popupRef.current.remove();
        popupRef.current = new window.mapboxgl.Popup({ offset: 6, closeButton: true })
          .setLngLat(e.lngLat)
          .setHTML(markerPopupHTML({
            detailType:  props.detailType,
            dealName:    props.dealName,
            dealStatus:  props.dealStatus,
            detailStatus: props.detailStatus,
            entityValue: props.entityValue,
            regridAcres: props.regridAcres,
          }))
          .addTo(map);
      });
      map.on('mouseenter', 'parcel-polygons-fill', () => { map.getCanvas().style.cursor = 'pointer'; });
      map.on('mouseleave', 'parcel-polygons-fill', () => { map.getCanvas().style.cursor = ''; });
    }
    applyPolygonFilter(map);
  }, [geometries]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Apply polygon visibility filter ──────────────────────
  const applyPolygonFilter = (map) => {
    if (!map || !map.getLayer('parcel-polygons-fill')) return;
    const activeTypes = Object.entries(layers).filter(([, v]) => v).map(([k]) => k);
    if (activeTypes.length === 0) {
      map.setFilter('parcel-polygons-fill',    ['==', ['literal', true], false]);
      map.setFilter('parcel-polygons-outline', ['==', ['literal', true], false]);
      return;
    }
    const conditions = ['all'];
    if (activeTypes.length < Object.keys(LAYERS).length) {
      conditions.push(['any', ...activeTypes.map(t => ['==', ['get', 'detailType'], t])]);
    }
    if (dealFilter) {
      conditions.push(['any', ...[...dealFilter].map(id => ['==', ['get', 'dealId'], id])]);
    }
    map.setFilter('parcel-polygons-fill',    conditions.length > 1 ? conditions : null);
    map.setFilter('parcel-polygons-outline', conditions.length > 1 ? conditions : null);
  };

  useEffect(() => {
    if (mapReady && mapReady !== 'no-token') setupPolygons();
  }, [mapReady, setupPolygons]);

  useEffect(() => {
    applyPolygonFilter(mapRef.current);
  }, [layers, dealFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Place/refresh markers whenever map, data, or layer toggles change ──
  const placeMarkers = useCallback(() => {
    const map = mapRef.current;
    if (!map || !markers) return;

    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];
    if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; }

    markers.forEach(m => {
      if (!m.centroidLat || !m.centroidLng) return;
      if (!isVisible(m)) return;

      const cfg = LAYERS[m.detailType] || {};
      const el  = document.createElement('div');
      el.style.cssText = `
        width:14px;height:14px;border-radius:50%;
        background:${cfg.colorHex};
        border:2px solid #fff;
        cursor:pointer;
        box-shadow:0 1px 4px rgba(0,0,0,0.4);
        transition:transform 0.15s;
      `;
      el.addEventListener('mouseenter', () => { el.style.transform = 'scale(1.4)'; });
      el.addEventListener('mouseleave', () => { el.style.transform = 'scale(1)'; });

      const marker = new window.mapboxgl.Marker({ element: el })
        .setLngLat([m.centroidLng, m.centroidLat])
        .addTo(map);

      el.addEventListener('click', (e) => {
        e.stopPropagation();
        if (popupRef.current) popupRef.current.remove();
        popupRef.current = new window.mapboxgl.Popup({ offset: 12, closeButton: true })
          .setLngLat([m.centroidLng, m.centroidLat])
          .setHTML(markerPopupHTML(m))
          .addTo(map);
      });

      markersRef.current.push(marker);
    });
  }, [markers, isVisible]);

  useEffect(() => {
    if (mapReady && mapReady !== 'no-token') placeMarkers();
  }, [mapReady, placeMarkers]);

  // ── Setup/update annotation layers (read-only land features) ──
  const setupAnnotations = useCallback(() => {
    const map = mapRef.current;
    if (!map || !annotations) return;
    if (!showAnnotations) { removeAnnotationLayers(map); return; }
    const features = (annotations.features || []).filter(f => {
      const p = f.properties || {};
      if (p.detailType && layers[p.detailType] === false) return false;
      if (dealFilter && p.dealId && !dealFilter.has(p.dealId)) return false;
      return true;
    });
    setupAnnotationLayers(map, { type: 'FeatureCollection', features });
  }, [annotations, showAnnotations, layers, dealFilter]);

  useEffect(() => {
    if (mapReady && mapReady !== 'no-token') setupAnnotations();
  }, [mapReady, setupAnnotations]);

  // ── Rebuild app + overlay layers after a basemap swap ─────
  // setStyle() wipes every source/layer, so re-add overlays, terrain, parcel
  // polygons and markers once the new style finishes loading.
  const rebuildAfterStyle = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    reapplyOverlays(map, overlaysRef.current);
    if (terrainRef.current.on) enableTerrain(map, terrainRef.current.exaggeration);
    setupPolygons();
    placeMarkers();
    setupAnnotations();
  }, [setupPolygons, placeMarkers, setupAnnotations]);

  // ── Basemap switch ────────────────────────────────────────
  const changeBasemap = (id) => {
    const map = mapRef.current;
    if (!map || id === basemapId) return;
    if (!applyBasemap(map, id)) return;
    setBasemapId(id);
    map.once('style.load', rebuildAfterStyle);
  };

  // ── Overlay toggle / opacity ──────────────────────────────
  const toggleOverlay = (layer) => {
    const map = mapRef.current;
    setOverlays(prev => {
      const cur  = prev[layer.id];
      const on   = !cur?.on;
      const opacity = cur?.opacity ?? layer.defaultOpacity ?? 0.7;
      if (map) {
        if (on) addOverlay(map, layer, opacity);
        else    removeOverlay(map, layer);
      }
      return { ...prev, [layer.id]: { overlay: layer, on, opacity } };
    });
  };

  const changeOverlayOpacity = (layer, opacity) => {
    const map = mapRef.current;
    if (map) setOverlayOpacity(map, layer, opacity);
    setOverlays(prev => ({ ...prev, [layer.id]: { ...prev[layer.id], overlay: layer, opacity } }));
  };

  // ── 3D terrain ────────────────────────────────────────────
  const toggleTerrain = (on) => {
    const map = mapRef.current;
    if (map) { if (on) enableTerrain(map, terrain.exaggeration); else disableTerrain(map); }
    setTerrain(t => ({ ...t, on }));
  };

  const changeExaggeration = (exaggeration) => {
    const map = mapRef.current;
    if (map && terrain.on) setTerrainExaggeration(map, exaggeration);
    setTerrain(t => ({ ...t, exaggeration }));
  };

  // ── Fit to visible markers ────────────────────────────────
  const fitMarkers = () => {
    const map = mapRef.current;
    if (!map || !markers?.length) return;
    const visible = markers.filter(m => isVisible(m) && m.centroidLat && m.centroidLng);
    if (!visible.length) return;
    const lngs = visible.map(m => m.centroidLng);
    const lats = visible.map(m => m.centroidLat);
    map.fitBounds(
      [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
      { padding: 80, maxZoom: 14, duration: 800 }
    );
  };

  // ── Toggle individual deal ────────────────────────────────
  const toggleDeal = (dealId) => {
    setDealFilter(prev => {
      if (prev === null) {
        const all = new Set(dealList.map(d => d.dealId));
        all.delete(dealId);
        return all;
      }
      const next = new Set(prev);
      if (next.has(dealId)) next.delete(dealId);
      else next.add(dealId);
      return next.size === dealList.length ? null : next;
    });
  };

  const isDealVisible = (dealId) => dealFilter === null || dealFilter.has(dealId);

  // ── Counts ────────────────────────────────────────────────
  const counts = (markers || []).reduce((acc, m) => {
    acc[m.detailType] = (acc[m.detailType] || 0) + 1;
    return acc;
  }, {});

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 80px)', gap: 0 }}>

      {/* ── Toolbar ─────────────────────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
        padding: '12px 0', borderBottom: `1px solid ${B.border}`, marginBottom: 8,
      }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: B.text, marginRight: 8 }}>Map</div>

        {/* Layer toggles */}
        {Object.entries(LAYERS).map(([type, cfg]) => (
          <button key={type} onClick={() => setLayers(l => ({ ...l, [type]: !l[type] }))} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 14px', borderRadius: 20,
            border: `1px solid ${layers[type] ? cfg.color : B.border}`,
            background: layers[type] ? cfg.color : B.white,
            color: layers[type] ? B.white : B.textMd,
            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            transition: 'all 0.15s',
          }}>
            <span style={{
              width: 8, height: 8, borderRadius: '50%',
              background: layers[type] ? B.white : cfg.color,
              display: 'inline-block',
            }} />
            {cfg.label}
            {counts[type] != null && (
              <span style={{ opacity: 0.8 }}>({counts[type]})</span>
            )}
          </button>
        ))}

        {/* Filter panel toggle */}
        <button onClick={() => setShowFilter(f => !f)} style={{
          padding: '6px 14px', borderRadius: 20,
          border: `1px solid ${showFilter ? B.tealDk : B.border}`,
          background: showFilter ? B.tealDk : B.white,
          color: showFilter ? B.white : B.textMd,
          fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          transition: 'all 0.15s',
        }}>
          {dealFilter ? '⊘ Filtered' : '⊟ Filter'}
        </button>

        {/* Land features (annotations) toggle */}
        <button onClick={() => setShowAnnotations(a => !a)} style={{
          padding: '6px 14px', borderRadius: 20,
          border: `1px solid ${showAnnotations ? B.green : B.border}`,
          background: showAnnotations ? B.green : B.white,
          color: showAnnotations ? B.white : B.textMd,
          fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          transition: 'all 0.15s',
        }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="land" size={14} /> Land Features</span>
        </button>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={fitMarkers} style={{
            padding: '6px 12px', borderRadius: 6, border: `1px solid ${B.border}`,
            background: B.white, color: B.textMd, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit',
          }}>Fit All</button>

          <button onClick={() => setShowLayers(s => !s)} style={{
            padding: '6px 14px', borderRadius: 20,
            border: `1px solid ${showLayers ? B.navy : B.border}`,
            background: showLayers ? B.navy : B.white,
            color: showLayers ? B.white : B.textMd,
            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            transition: 'all 0.15s',
          }}><span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Icon name="layers" size={14} /> Layers</span></button>
        </div>
      </div>

      {/* ── Map + filter panel row ───────────────────────────── */}
      <div style={{ display: 'flex', flex: 1, gap: 8, minHeight: 0 }}>

        {/* ── Filter panel ──────────────────────────────────── */}
        {showFilter && (
          <div style={{
            width: 240, flexShrink: 0, background: B.white, border: `1px solid ${B.border}`,
            borderRadius: 10, padding: 14, overflowY: 'auto', fontSize: 12, color: B.text,
          }}>
            {/* Clear all */}
            {dealFilter && (
              <button onClick={() => setDealFilter(null)} style={{
                width: '100%', padding: '5px 0', marginBottom: 10, borderRadius: 6,
                border: `1px solid ${B.border}`, background: B.surfaceMuted,
                color: B.textMd, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit',
              }}>Clear All Filters</button>
            )}

            {/* Deals grouped by type */}
            {Object.entries(LAYERS).map(([type, cfg]) => {
              const typeDeals = dealList.filter(d => d.detailType === type);
              if (typeDeals.length === 0) return null;
              return (
                <div key={type}>
                  <div style={{ fontWeight: 700, color: cfg.colorHex, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: 10, margin: '12px 0 6px' }}>{cfg.label}</div>
                  {typeDeals.map(d => (
                    <label key={d.dealId} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', cursor: 'pointer' }}>
                      <input type="checkbox" checked={isDealVisible(d.dealId)} onChange={() => toggleDeal(d.dealId)} style={{ cursor: 'pointer' }} />
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={d.dealName}>{d.dealName}</span>
                    </label>
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {/* ── Map container ───────────────────────────────────── */}
        <div style={{ position: 'relative', flex: 1, borderRadius: 10, overflow: 'hidden', border: `1px solid ${B.border}`, minWidth: 0 }}>
          {!MAPBOX_TOKEN && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: B.surfaceMuted, zIndex: 10 }}>
              <div style={{ textAlign: 'center', color: B.textMd, fontSize: 14 }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8, color: B.textLt }}><Icon name="map" size={26} strokeWidth={1.75} /></div>
                Set <code>REACT_APP_MAPBOX_TOKEN</code> to enable the map.
              </div>
            </div>
          )}
          {loading && (
            <div style={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 10, background: B.white, borderRadius: 8, padding: '8px 16px', boxShadow: B.shadowMd }}>
              <Spinner />
            </div>
          )}
          {error && (
            <div style={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 10, background: '#fff0f0', border: `1px solid ${B.red}44`, borderRadius: 8, padding: '8px 16px', color: B.red, fontSize: 13 }}>
              {error}
            </div>
          )}
          <div ref={mapContainer} style={{ width: '100%', height: '100%' }} />
        </div>

        {/* ── Layer catalog panel ─────────────────────────────── */}
        {showLayers && (
          <MapLayerCatalog
            basemapId={basemapId}
            onBasemapChange={changeBasemap}
            overlays={overlays}
            onToggleOverlay={toggleOverlay}
            onOverlayOpacity={changeOverlayOpacity}
            terrain={terrain}
            onTerrainToggle={toggleTerrain}
            onExaggerationChange={changeExaggeration}
          />
        )}
      </div>

      {/* ── Status bar ──────────────────────────────────────── */}
      <div style={{ fontSize: 11, color: B.textLt, paddingTop: 6, textAlign: 'right' }}>
        {markers
          ? `${markersRef.current.length} marker${markersRef.current.length !== 1 ? 's' : ''} visible of ${markers.length} total`
          : 'Loading markers…'}
      </div>
    </div>
  );
};
